import React from 'react';
import { Stack, Typography, Box, Chip, Button } from '@mui/material';
import { Warning, Download } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useLazyGetAugmentationFileDownloadPathQuery } from '../../../../api/organizationSlice';
import { ORG_EXPECTED_FILES_WITH_HEADERS } from './ExpectedFilesList';
import { formatFileSize, getHeaders } from './Controllers/fileUtils';

function FileItem({ 
    file, 
    pendingReplacement = null,
    organizationId = null,
    fileType = null,
    fileHeaderResponse = null, // Contains {headers: [...], fileName: "", fileType: ""}
    fileMetadataResponse = null // Contains {size: number, last_modified: string, fileName: "", fileType: ""}
}) {
    const theme = useTheme();
    const [getAugmentationFileDownloadPath, { isFetching: isDownloading }] = useLazyGetAugmentationFileDownloadPathQuery();
    
    // Function to get file type name based on headers
    const getFileTypeName = (headerArray) => {
        if (!organizationId || !headerArray || !Array.isArray(headerArray)) return null;
        
        const expectedFiles = ORG_EXPECTED_FILES_WITH_HEADERS[organizationId] || {};
        
        for (const [fileType, expectedHeaders] of Object.entries(expectedFiles)) {
            const headersMatch = headerArray.length === expectedHeaders.length && 
                headerArray.every(header => expectedHeaders.includes(header));
            if (headersMatch) {
                return fileType;
            }
        }
        return null;
    };

    const willBeReplaced = pendingReplacement && file.file_name === pendingReplacement.file_name && !file.isPending;
    
    // Determine which headers to use and file type name
    let fileHeaders = null;
    let fileTypeName = null;
    
    if (file.isPending && file.header) {
        // For pending files, use the header field and parse it
        fileHeaders = getHeaders(file.header);
        fileTypeName = getFileTypeName(fileHeaders);
    } else if (fileHeaderResponse && fileHeaderResponse.headers && Array.isArray(fileHeaderResponse.headers)) {
        // For uploaded files, extract headers array from API response object
        fileHeaders = fileHeaderResponse.headers;
        fileTypeName = getFileTypeName(fileHeaders);
    }

    const canDownloadAugmentationFile = String(fileType).toLowerCase() === 'augmentation' && organizationId && file.file_name && !file.isPending;

    const handleDownload = async () => {
        if (!canDownloadAugmentationFile) {
            return;
        }

        try {
            const response = await getAugmentationFileDownloadPath({
                organizationId,
                fileName: file.file_name
            }).unwrap();

            if (response?.filepath) {
                window.open(response.filepath, '_blank', 'noopener,noreferrer');
            }
        } catch (error) {
            console.error('Augmentation file download failed:', error);
        }
    };
    
    return (
        <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center"
            sx={{ 
                p: 1, 
                backgroundColor: willBeReplaced 
                    ? theme.palette.background.yellow
                    : file.isPending 
                        ? theme.palette.background.green
                        : theme.palette.background.white, 
                '&:hover': { 
                    backgroundColor: willBeReplaced 
                        ? theme.palette.background.darkYellow
                        : file.isPending 
                            ? theme.palette.background.darkGreen
                            : theme.palette.background.neutral 
                },
                borderRadius: 1,
                border: willBeReplaced 
                    ? `1px solid ${theme.palette.border.yellow}` 
                    : file.isPending 
                        ? `1px solid ${theme.palette.border.green}` 
                        : `1px solid ${theme.palette.border.main}`
            }}
        >
            <Stack spacing={0.5}>
                <Stack direction='row' spacing={1} alignItems='center'>
                    <Stack spacing={0.25}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            {fileTypeName && (
                                <Chip 
                                    label={fileTypeName}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                    sx={{
                                        fontSize: '10px',
                                        height: '18px',
                                        '& .MuiChip-label': { px: 0.5 }
                                    }}
                                />
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {file.file_name}
                            </Typography>
                        </Stack>
                        {fileHeaders && (!organizationId || !fileTypeName) && (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                Header: {fileHeaders.join(', ')}
                            </Typography>
                        )}
                        {!fileHeaders && !file.isPending && organizationId && (
                            <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                Loading headers...
                            </Typography>
                        )}
                    </Stack>
                    {file.isPending && (
                        <Chip 
                            label="Pending Upload" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                        />
                    )}
                    {willBeReplaced && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <Warning fontSize='16px' sx={{ color: theme.palette.yellow.inProgress }} />
                            <Chip 
                                label="Will be replaced" 
                                size="small" 
                                color="warning" 
                                variant="outlined"
                            />
                        </Stack>
                    )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                    {(() => {
                        // For pending files, use the file object data
                        if (file.isPending) {
                            const size = file.size || 0;
                            const lastModified = file.last_modified || new Date().toISOString();
                            const modifiedDate = new Date(lastModified);
                            return `Size: ${formatFileSize(size)} | Modified: ${modifiedDate.toLocaleDateString()} ${modifiedDate.toLocaleTimeString()}`;
                        }
                        
                        // For uploaded files, use metadata API response
                        if (fileMetadataResponse && fileMetadataResponse.metadata) {
                            // Extract metadata from the nested metadata property
                            const metadata = fileMetadataResponse.metadata;
                            const size = metadata.size ?? metadata.fileSize ?? 0;
                            const lastModified = metadata.last_modified ?? metadata.lastModified ?? metadata.modified;
                            
                            if (size !== undefined && lastModified) {
                                const modifiedDate = new Date(lastModified);
                                return `Size: ${formatFileSize(size)} | Modified: ${modifiedDate.toLocaleDateString()} ${modifiedDate.toLocaleTimeString()}`;
                            }
                        }
                        
                        // Show loading message for uploaded files without metadata
                        if (!file.isPending && organizationId) {
                            return 'Loading metadata...';
                        }
                        
                        return 'Metadata unavailable';
                    })()}
                </Typography>
            </Stack>

            {canDownloadAugmentationFile && (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDownload}
                    disabled={isDownloading}
                    startIcon={<Download />}
                    sx={{ ml: 2, flexShrink: 0 }}
                >
                    {isDownloading ? 'Loading...' : 'Download'}
                </Button>
            )}
        </Box>
    );
}

export default FileItem;